import _ from 'lodash';

import type { Approval } from '../../../repositories/approvalRepository';
import { formatDate } from '../../../utils/dateUtils';
import { getCancelledWord } from '../../../utils/translationUtil';
import { sortApprovalsOptions } from '../../../zustandStore/slices';
import type { SingleSortOption, SortApprovalsKey, User } from '../../../zustandStore/slices';

export const isProcessing = (approval: Approval) => {
	if (_.isString(approval?.context?.isProcessing)) {
		return approval?.context?.isProcessing === 'true';
	}
	return approval?.context?.isProcessing;
};

export const approvalStatusToLabel = {
	approved: 'Approved',
	'card-canceled': `Card ${getCancelledWord('canceled')}`,
	canceled: `Card ${getCancelledWord('canceled')}`,
	declined: 'Declined',
	'forwarded-pending': 'Forwarded (pending)',
	pending: 'Pending',
	'pending-forwarded': 'Pending (forwarded)',
};

export const isReassignedToSelf = (approval: Approval) => {
	if (_.isString(approval.context.reassignedToSelf)) {
		return approval.context.reassignedToSelf === 'true';
	}
	return approval.context.reassignedToSelf;
};

export const getForwardedToYouMessage = (approval: Approval, currentUser: User): string => {
	const assignedOn = formatDate(approval.assignedOn, currentUser?.language);
	if (isReassignedToSelf(approval)) {
		return `This request was re-assigned to you on ${assignedOn}.`;
	}

	return approval.outcome === 'SELF-REASSIGNED'
		? 'This request has been reassigned.'
		: `${approval.reassignedByUser?.fullName} forwarded this request to you on ${assignedOn}.`;
};

export const getForwardedByYouMessage = (approval: Approval, currentUser: User) => {
	if (!approval.completedOn) {
		return '';
	}
	const completedOn = formatDate(approval.completedOn, currentUser?.language);
	return approval.context.isAutoForwarded === 'true'
		? `This request was automatically forwarded to ${approval.reassignedToUser?.fullName} on ${completedOn}.`
		: `You forwarded this request to ${approval.reassignedToUser?.fullName} on ${completedOn}.`;
};

function getComparableValue(approval: Approval, sortKey: SortApprovalsKey) {
	switch (sortKey) {
		case 'mostRecent': {
			return new Date(approval.request.lastEdit);
		}
		case 'requester': {
			return approval.request.requester.firstName.toLowerCase();
		}
		case 'lowToHigh':
		case 'highToLow': {
			return approval.request.amount;
		}
		default: {
			return approval;
		}
	}
}

export function filterActivitiesByUserFilters(
	approvals: Approval[],
	filters: {
		team: string;
		member: string;
	},
) {
	return approvals
		?.filter(approval => filters.team === 'all' || (filters.team !== 'all' && approval.request.teamId === filters.team))
		?.filter(
			approval => filters.member === 'all' || (filters.member !== 'all' && approval.assigner === filters.member),
		);
}

export function filterApprovalsByTabAndSearch(approvals: Approval[], searchValue: string) {
	const searchLower = searchValue.toLowerCase();
	return approvals?.filter(approval => approval.request.title.toLowerCase().includes(searchLower));
}

export function sortApprovals(approvals: Approval[], sortValue: SingleSortOption) {
	if (!sortValue) {
		return approvals;
	}

	const [sortKey = 'mostRecent'] = Object.keys(sortValue) as SortApprovalsKey[];
	const sortOrder = sortApprovalsOptions[sortKey];

	return approvals?.toSorted((a, b) => {
		const valueA = getComparableValue(a, sortKey);
		const valueB = getComparableValue(b, sortKey);

		if (sortOrder === 'asc') {
			return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
		} else {
			return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
		}
	});
}

export function getEmptyMessage(approvalState: string) {
	switch (approvalState) {
		case 'pending': {
			return 'You have no pending approvals.';
		}
		case 'approved': {
			return 'You have no approved approvals.';
		}
		case 'declined': {
			return 'You have no declined approvals.';
		}
		case 'canceled': {
			return `You have no ${getCancelledWord('canceled')} approvals.`;
		}
		default: {
			return 'You have no approvals.';
		}
	}
}
